import type { NeedCategory, NeedRecurrence, NeedUrgency } from '@/lib/types'

/**
 * Common one-tap needs. Instead of forcing the parent to fill an 8-field
 * form for a routine reminder, we present these as chips at the top of
 * /needs/new. Tap = title + category + urgency + due-time + recurrence
 * all pre-filled. User can still edit before saving.
 */
export interface NeedPreset {
  id: string
  emoji: string
  label: string
  title: string
  category: NeedCategory
  urgency: NeedUrgency
  /** Hours from "now" to default the due date to. Undefined = leave due blank. */
  dueOffsetHours?: number
  /** If a hard time-of-day (24h clock) is more sensible than an offset, use this instead. */
  dueTimeOfDay?: string
  recurrence?: NeedRecurrence
}

export const NEED_PRESETS: NeedPreset[] = [
  { id: 'work',          emoji: '💼', label: 'Work',           title: 'Work',            category: 'other',       urgency: 'normal', recurrence: 'weekdays', dueTimeOfDay: '09:00' },
  { id: 'daycare_drop',  emoji: '🚌', label: 'Daycare drop',   title: 'Daycare drop-off', category: 'logistics',   urgency: 'high',   recurrence: 'weekdays', dueTimeOfDay: '08:00' },
  { id: 'daycare_pick',  emoji: '🚗', label: 'Daycare pickup', title: 'Daycare pickup',   category: 'logistics',   urgency: 'high',   recurrence: 'weekdays', dueTimeOfDay: '17:00' },
  { id: 'grocery',       emoji: '🛒', label: 'Grocery',        title: 'Grocery run',      category: 'logistics',   urgency: 'normal', recurrence: 'weekly',   dueTimeOfDay: '10:00' },
  { id: 'bath',          emoji: '🛁', label: 'Bath',           title: 'Bath time',        category: 'activity',    urgency: 'normal', recurrence: 'daily',    dueTimeOfDay: '18:30' },
  { id: 'pediatric',     emoji: '🩺', label: 'Doctor visit',   title: 'Pediatric visit',  category: 'appointment', urgency: 'high',   dueOffsetHours: 72 },
  { id: 'medication',    emoji: '💊', label: 'Medication',     title: 'Give medication',  category: 'health',      urgency: 'high',   recurrence: 'daily',    dueTimeOfDay: '08:00' },
  { id: 'refill',        emoji: '📦', label: 'Refill supplies', title: 'Refill diapers/wipes/formula', category: 'equipment', urgency: 'normal', dueOffsetHours: 24 },
]

// Keyword → category. Cheap-and-cheerful; wrong guesses are easy to override in the form.
// Prefixes are matched (e.g. "pediatr" matches "pediatrician" and "pediatric").
const CATEGORY_KEYWORDS: Array<[string, NeedCategory]> = [
  // Appointments / medical events
  ['pediatr', 'appointment'], ['doctor', 'appointment'], ['dentist', 'appointment'],
  ['appointment', 'appointment'], ['appt', 'appointment'], ['checkup', 'appointment'],
  ['visit', 'appointment'], ['ultrasound', 'appointment'], ['obgyn', 'appointment'],
  // Health / medication
  ['medication', 'health'], ['medicine', 'health'], ['prescription', 'health'],
  ['vitamin', 'health'], ['tylenol', 'health'], ['dose', 'health'],
  ['fever', 'health'], ['sick', 'health'], ['vaccine', 'health'], ['immuniz', 'health'],
  // Equipment / supplies
  ['diaper', 'equipment'], ['wipes', 'equipment'], ['formula', 'equipment'],
  ['bottle', 'equipment'], ['crib', 'equipment'], ['carseat', 'equipment'],
  ['stroller', 'equipment'], ['refill', 'equipment'],
  // Clothing
  ['clothes', 'clothing'], ['pajama', 'clothing'], ['socks', 'clothing'],
  ['onesie', 'clothing'], ['shoes', 'clothing'], ['jacket', 'clothing'],
  // Logistics
  ['grocery', 'logistics'], ['shop', 'logistics'], ['pickup', 'logistics'],
  ['dropoff', 'logistics'], ['daycare', 'logistics'], ['errand', 'logistics'],
  // Activity
  ['bath', 'activity'], ['play', 'activity'], ['park', 'activity'],
  ['walk', 'activity'], ['music', 'activity'], ['story', 'activity'],
  // Financial
  ['bill', 'financial'], ['pay ', 'financial'], ['invoice', 'financial'],
  ['deposit', 'financial'], ['tax', 'financial'], ['insurance', 'financial'],
  // School
  ['school', 'education'], ['preschool', 'education'], ['tuition', 'education'],
  // Safety
  ['safety', 'safety'], ['gate', 'safety'], ['outlet', 'safety'], ['babyproof', 'safety'],
  // Documents
  ['passport', 'document'], ['birth certificate', 'document'], ['sin', 'document'],
  ['health card', 'document'], ['photo id', 'document'], ['ssn', 'document'],
  // Maintenance
  ['clean', 'maintenance'], ['laundry', 'maintenance'], ['dishes', 'maintenance'],
  ['sanitize', 'maintenance'], ['maintenance', 'maintenance'],
  // Feeding-specific (higher-priority than "equipment" since it maps to the feeding pack)
  ['breastfeed', 'feeding'], ['pump ', 'feeding'], ['solids', 'feeding'],
  ['puree', 'feeding'], ['bottle warm', 'feeding'],
]

/** Best-effort category guess from a title string. Returns null if no keyword matches. */
export function guessCategoryFromTitle(title: string): NeedCategory | null {
  const t = ` ${title.toLowerCase()} `
  for (const [keyword, category] of CATEGORY_KEYWORDS) {
    if (t.includes(keyword)) return category
  }
  return null
}

/** Compute the `due_at` ISO string for a preset relative to `now`. */
export function computeDueAt(preset: NeedPreset, now = new Date()): string | null {
  if (preset.dueTimeOfDay) {
    const [h, m] = preset.dueTimeOfDay.split(':').map(Number)
    const d = new Date(now)
    d.setHours(h, m, 0, 0)
    // If that time already passed today, roll forward one day.
    if (d.getTime() <= now.getTime()) d.setDate(d.getDate() + 1)
    return d.toISOString()
  }
  if (preset.dueOffsetHours != null) {
    return new Date(now.getTime() + preset.dueOffsetHours * 3_600_000).toISOString()
  }
  return null
}

/** Human-readable label for the recurrence enum. */
export function recurrenceLabel(r: NeedRecurrence | null | undefined): string | null {
  if (!r) return null
  switch (r) {
    case 'daily':    return 'Every day'
    case 'weekdays': return 'Mon–Fri'
    case 'weekends': return 'Sat–Sun'
    case 'weekly':   return 'Every week'
    case 'monthly':  return 'Every month'
  }
}
